import { useEffect, useState } from 'react'
import { api, type PersonContact } from './api'

// Who a stored subject is, for display. An owner is stored as a subject so it
// survives an address change, and was shown as that subject: a uuid where a
// reader expected a person. One read per page load, shared by every screen,
// because the answer changes only when somebody joins or leaves.

type Directory = { names: Record<string, string>; people: Record<string, PersonContact> }

let pending: Promise<Directory> | null = null

function load(): Promise<Directory> {
  if (!pending) {
    pending = api
      .peopleNames()
      .then((r) => {
        const names = r.names ?? {}
        // An API from before the people map answers with names alone. Each
        // address is still a contact, with no name, which is what it is.
        const people: Record<string, PersonContact> = r.people ?? {}
        if (!r.people) for (const [subject, email] of Object.entries(names)) people[subject] = { email }
        return { names, people }
      })
      .catch(() => {
        // Not an error worth a banner: the screen shows what is stored, as it
        // did before. Forget the failure so the next screen asks again.
        pending = null
        return { names: {}, people: {} }
      })
  }
  return pending
}

function useDirectory(): Directory {
  const [dir, setDir] = useState<Directory>({ names: {}, people: {} })
  useEffect(() => {
    let live = true
    void load().then((d) => {
      if (live) setDir(d)
    })
    return () => {
      live = false
    }
  }, [])
  return dir
}

export function usePeopleNames(): Record<string, string> {
  return useDirectory().names
}

// The same read with the name beside the address where the directory has one.
export function usePeopleContacts(): Record<string, PersonContact> {
  return useDirectory().people
}

// The address for a subject the directory knows, and the stored value
// otherwise: a free-text name such as "R. Iyer" is already what to show.
export function personLabel(names: Record<string, string>, stored: string | null | undefined): string {
  const v = (stored ?? '').trim()
  return names[v] ?? v
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Who answers for a vendor: the owner, and the business owner where there is
// none. The same rule the portfolio export applies on the server, so the
// screen and the file name the same person. Never an identifier: a subject
// the directory does not know says so, and nobody named is Unassigned. The
// phone is the work phone kept in People and roles, where one is on file for
// a person the directory knows.
export type OwnerContact = { name: string; email: string; phone: string; state: 'PERSON' | 'UNKNOWN' | 'UNASSIGNED' }

// A stored phone as a person reads it: an Indian number in two groups of five
// after +91, the way the server writes it into the portfolio workbook, and
// anything else as stored.
export function phoneWords(e164: string | null | undefined): string {
  const v = (e164 ?? '').trim()
  const m = /^\+91([1-9][0-9]{4})([0-9]{5})$/.exec(v)
  return m ? '+91 ' + m[1] + ' ' + m[2] : v
}

export function ownerContact(
  people: Record<string, PersonContact>,
  owner: string | null | undefined,
  businessOwner: string | null | undefined,
): OwnerContact {
  const stored = [owner, businessOwner].map((v) => (v ?? '').trim()).find((v) => v !== '') ?? ''
  if (stored === '') return { name: 'Unassigned', email: '', phone: '', state: 'UNASSIGNED' }
  const known = people[stored]
  if (known) return { name: known.name ?? '', email: known.email, phone: known.phone ?? '', state: 'PERSON' }
  if (EMAIL.test(stored)) {
    const byAddress = Object.values(people).find((p) => p.email.toLowerCase() === stored.toLowerCase())
    return { name: byAddress?.name ?? '', email: byAddress?.email ?? stored, phone: byAddress?.phone ?? '', state: 'PERSON' }
  }
  if (UUID.test(stored)) return { name: 'Not in the directory', email: '', phone: '', state: 'UNKNOWN' }
  return { name: stored, email: '', phone: '', state: 'PERSON' }
}
